import { type ReactElement } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useThemeContext } from '@/hooks/theme/useThemeContext';
import { usePreferencesQuery } from '@/hooks/preferences/usePreferencesQuery';
import { useTimestampFormat } from '@/hooks/useTimestampFormat';
import { THEME_PREFERENCES } from '@/styles/constants';
import type { ThemePreferenceType } from '@/styles/types';
import type { TimestampFormat } from '@/contexts/TimestampFormatContext';
import { Section } from './Section';
import { PreferencesSyncNotice } from './PreferencesSyncNotice';
import { ThemeSyncBadge } from './ThemeSyncBadge';
import { TimeZoneControl } from './TimeZoneControl';
import { AbsoluteFormatControl } from './AbsoluteFormatControl';
import { RadioButtonGroup, type RadioButtonOption } from './RadioButtonGroup';

const THEME_OPTIONS: readonly RadioButtonOption<ThemePreferenceType>[] = [
  { value: THEME_PREFERENCES.DARK, label: 'Dark', icon: Moon },
  { value: THEME_PREFERENCES.LIGHT, label: 'Light', icon: Sun },
  { value: THEME_PREFERENCES.SYSTEM, label: 'System', icon: Monitor },
] as const;

const TIMESTAMP_FORMAT_OPTIONS: readonly RadioButtonOption<TimestampFormat>[] =
  [
    { value: 'relative', label: 'Relative' },
    { value: 'absolute', label: 'Absolute' },
  ] as const;

export function PreferencesSubTab(): ReactElement {
  const {
    value: activeTheme,
    onChange,
    isPending,
    hasSyncError,
    retrySync,
  } = useThemeContext();
  const { isError } = usePreferencesQuery();
  const { value: timestampFormat, setValue: setTimestampFormat } =
    useTimestampFormat();

  return (
    <div className="space-y-7" data-testid="preferences-subtab">
      {isError && <PreferencesSyncNotice />}
      <Section
        title="Appearance"
        description="Customize the appearance of the Kirin interface"
      >
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Theme</CardTitle>
              <CardDescription>
                Set your preferred theme for the interface
              </CardDescription>
            </CardHeader>
            <CardContent className="px-6">
              <RadioButtonGroup
                aria-label="Theme preference"
                options={THEME_OPTIONS}
                value={activeTheme}
                onChange={onChange}
                disabled={isPending}
              />
              {hasSyncError && (
                <ThemeSyncBadge onRetry={retrySync} isRetrying={isPending} />
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Timestamp Format</CardTitle>
              <CardDescription>
                Set your preferred format for timestamps
              </CardDescription>
            </CardHeader>
            <CardContent className="px-6">
              <RadioButtonGroup
                aria-label="Timestamp format"
                options={TIMESTAMP_FORMAT_OPTIONS}
                value={timestampFormat}
                onChange={setTimestampFormat}
              />
              <div className="mt-5">
                <AbsoluteFormatControl />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Time Zone</CardTitle>
              <CardDescription>
                Set your preferred time zone for timestamps
              </CardDescription>
            </CardHeader>
            <CardContent className="px-6">
              <TimeZoneControl />
            </CardContent>
          </Card>
        </div>
      </Section>
    </div>
  );
}
